import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowLeft } from "lucide-react";
import { BDK, waUrl, SITE_URL, NEXO_ENABLED } from "../config/bdk";
import useReveal from "../hooks/useReveal";
import Seo from "../components/Seo";
import Services from "../components/landing/Services";
import ImageExamples from "../components/landing/ImageExamples";
import VideoExamples from "../components/landing/VideoExamples";
import IndividualServices from "../components/landing/IndividualServices";
import { Footer } from "../components/landing/FinalCTAFooter";
import WhatsAppFloat from "../components/landing/WhatsAppFloat";
import WhatsAppLeadModal from "../components/landing/WhatsAppLeadModal";

// Packages that bundle multiple creative-content pieces (moved here from the
// old home "Paquetes" section — see Pricing.jsx, which now covers web-page
// pricing instead). "Despegue" bundles a landing page with creative assets;
// it stays here because its primary sell is the visual-content volume.
const packages = [
  {
    id: "arranque",
    badge: "Para empezar",
    name: "Arranque",
    price: "1,490",
    ideal:
      "Para empezar a publicar contenido más profesional sin invertir demasiado.",
    features: [
      "10 imágenes publicitarias",
      "Diseños para Facebook, Instagram y WhatsApp",
      "Copy persuasivo incluido",
      "Una ronda de ajustes",
    ],
    delivery: "5 días hábiles",
    ctaText: "Quiero Arranque",
    ctaMsg:
      "Hola, quiero información sobre el paquete Arranque ($1,490 MXN) de BDKSISTEMAS.",
    variant: "outline",
  },
  {
    id: "impulso",
    badge: "Más recomendado",
    name: "Impulso",
    price: "2,990",
    ideal:
      "Contenido visual completo para promocionar servicios y campañas durante el mes.",
    features: [
      "10 imágenes publicitarias",
      "2 videos publicitarios con IA",
      "10 imágenes tipo fotografía con IA",
      "Copy persuasivo incluido",
      "Una ronda de ajustes",
    ],
    delivery: "7 días hábiles",
    ctaText: "Quiero Impulso",
    ctaMsg:
      "Hola, quiero información sobre el paquete Impulso ($2,990 MXN) de BDKSISTEMAS.",
    variant: "primary",
    featured: true,
  },
  {
    id: "despegue",
    badge: "Web + contenido",
    name: "Despegue",
    price: "5,990",
    ideal:
      "Presencia digital completa: landing page más material visual para promocionarte mejor.",
    features: [
      "Landing page profesional",
      "Dominio .com por 1er año*",
      "WhatsApp directo",
      "10 imágenes publicitarias",
      "2 videos publicitarios con IA",
      "10 imágenes tipo fotografía con IA",
      "Optimización móvil y escritorio",
    ],
    delivery: "10 días hábiles",
    note:
      "*Sujeto a disponibilidad. Con dominio/hosting del cliente: $5,490 MXN.",
    ctaText: "Quiero Despegue",
    ctaMsg:
      "Hola, quiero información sobre el Paquete Despegue ($5,990 MXN) de BDKSISTEMAS.",
    variant: "outline",
    premium: true,
  },
];

const CreativePackages = () => {
  useReveal();
  return (
    <section
      id="paquetes-creativos"
      data-testid="section-paquetes-creativos"
      className="bdk-section bdk-split-bg--reverse"
    >
      <div className="container-bdk">
        <div className="max-w-[760px] mx-auto text-center mb-16 bdk-reveal bdk-reveal--up">
          <p className="kicker">Paquetes de contenido</p>
          <h2 className="font-display text-[38px] sm:text-[48px] md:text-[56px] lg:text-[64px]">
            Paquetes de imágenes y video
            <br />
            listos para publicar.
          </h2>
          <p className="mt-5 text-[#666873] text-[15px] md:text-[16px]">
            Precios cerrados. Trabajo claro. Sin sorpresas.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-3 items-stretch">
          {packages.map((p, i) => {
            const featured = p.featured;
            const premium = p.premium;
            const cardStyle = premium
              ? {
                  background: "var(--ink)",
                  color: "#fff",
                  borderColor: "var(--ink)",
                }
              : featured
              ? {
                  border: "2px solid var(--red)",
                  boxShadow: "0 22px 55px rgba(217,0,32,.13)",
                }
              : { border: "1px solid var(--line)", background: "#fff" };
            const idealColor = premium ? "#b8b9c1" : "#666873";
            const priceColor = premium ? "#fff" : "var(--red)";

            return (
              <article
                key={p.id}
                data-testid={`pricing-card-${p.id}`}
                className={`relative flex flex-col p-8 rounded-[14px] bdk-reveal bdk-reveal--up ${
                  featured ? "md:-translate-y-3 featured-pulse" : ""
                }`}
                style={{
                  ...cardStyle,
                  "--reveal-delay": `${i * 90}ms`,
                }}
              >
                <div
                  className="absolute -top-[15px] left-1/2 -translate-x-1/2 px-3.5 py-1.5 rounded-full whitespace-nowrap"
                  style={{
                    background: "var(--red)",
                    color: "#fff",
                    fontSize: "10px",
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: ".5px",
                  }}
                >
                  {p.badge}
                </div>

                <p className="font-display text-[29px] font-black uppercase mt-2">
                  {p.name}
                </p>

                <div
                  className="font-display font-black leading-none my-4"
                  style={{ color: priceColor, fontSize: "54px" }}
                >
                  <span className="text-[25px] align-top mr-1">$</span>
                  {p.price}
                  <small className="text-[12px] ml-1 font-black">MXN</small>
                </div>

                <p
                  className="text-[13px] min-h-[70px]"
                  style={{ color: idealColor }}
                >
                  {p.ideal}
                </p>

                <ul className="flex-1 grid gap-2 my-6 list-none">
                  {p.features.map((f, j) => (
                    <li
                      key={j}
                      className="relative pl-5 text-[13px]"
                      style={{ color: premium ? "#e2e2e8" : "inherit" }}
                    >
                      <Check
                        size={14}
                        className="absolute left-0 top-[3px]"
                        style={{ color: "var(--red)" }}
                      />
                      {f}
                    </li>
                  ))}
                </ul>

                <p
                  className="text-[12px] mb-4"
                  style={{ color: premium ? "#b8b9c1" : "inherit" }}
                >
                  Entrega: <strong>{p.delivery}</strong>
                </p>

                {p.note && (
                  <p className="text-[10px] mb-4" style={{ color: "#999aa2" }}>
                    {p.note}
                  </p>
                )}

                <a
                  href={waUrl(p.ctaMsg)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`pricing-cta-${p.id}`}
                  className={`bdk-btn ${
                    p.variant === "primary"
                      ? "bdk-btn--primary"
                      : premium
                      ? "bdk-btn--ghost-dark"
                      : "bdk-btn--outline"
                  }`}
                >
                  {p.ctaText}
                </a>
              </article>
            );
          })}
        </div>

        <p className="mt-8 text-center text-[12px] text-[#666873] max-w-2xl mx-auto">
          Todos los precios están en pesos mexicanos y pueden cambiar sin
          previo aviso. Servicios menores a $2,000 MXN se pagan al 100%.
          Paquetes mayores inician con 50% de anticipo.
        </p>
      </div>
    </section>
  );
};

const OtherServicesHero = () => (
  <section
    data-testid="section-other-services-hero"
    className="bdk-section"
    style={{ background: "var(--paper)" }}
  >
    <div className="container-bdk max-w-[820px] mx-auto text-center">
      <p className="kicker">Otros servicios creativos</p>
      <h1 className="font-display text-[38px] sm:text-[48px] md:text-[58px]">
        Imágenes y video publicitario
        <br />
        con IA para tu negocio.
      </h1>
      <p className="mt-5 text-[#666873] text-[15px] md:text-[16px]">
        Contenido visual profesional para redes sociales, WhatsApp y
        anuncios, con o sin página web.
      </p>
    </div>
  </section>
);

const BackToHome = () => (
  <section
    data-testid="section-other-services-back"
    className="bdk-section"
    style={{ paddingTop: "0" }}
  >
    <div className="container-bdk text-center">
      <Link
        to="/"
        data-testid="other-services-bottom-back-home"
        className="bdk-btn bdk-btn--outline inline-flex items-center gap-2"
      >
        <ArrowLeft size={16} />
        Volver al inicio
      </Link>
    </div>
  </section>
);

const MiniHeader = () => (
  <header
    data-testid="other-services-header"
    className="relative"
    style={{ background: "var(--ink)", borderBottom: "1px solid #2a2b34" }}
  >
    <div className="container-bdk flex items-center justify-between gap-4 h-[72px]">
      <Link
        to="/"
        data-testid="other-services-logo"
        className="block w-[170px] md:w-[190px] flex-shrink-0"
      >
        <img
          src={BDK.logos.redWhite}
          alt="BDKSISTEMAS"
          className="w-full h-auto object-contain"
        />
      </Link>
      <Link
        to="/"
        data-testid="other-services-back-home"
        className="inline-flex items-center gap-2 text-white/80 hover:text-[#d90020] text-[12px] sm:text-[13px] font-bold uppercase tracking-wide transition-colors"
      >
        <ArrowLeft size={16} />
        <span className="hidden sm:inline">¿Necesitas una página web?</span>{" "}
        Volver al inicio
      </Link>
    </div>
  </header>
);

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL },
    {
      "@type": "ListItem",
      position: 2,
      name: "Otros servicios creativos",
      item: `${SITE_URL}/otros-servicios`,
    },
  ],
};

const OtherServices = () => {
  const [waHref, setWaHref] = React.useState(null);

  useEffect(() => {
    const onClick = (e) => {
      const link = e.target.closest('a[href*="wa.me"]');
      if (link && !link.hasAttribute("data-no-lead-modal")) {
        e.preventDefault();
        setWaHref(link.href);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <div
      data-testid="other-services-page"
      className="min-h-screen bg-white text-[#101010]"
    >
      <Seo
        title="Otros Servicios Creativos: Imágenes y Video con IA | BDKSISTEMAS"
        description="Imágenes publicitarias, video publicitario con IA e imágenes tipo fotografía con IA para promociones, redes sociales y anuncios de tu negocio."
        path="/otros-servicios"
        jsonLd={breadcrumbJsonLd}
      />
      <MiniHeader />
      <main>
        <OtherServicesHero />
        <Services />
        <ImageExamples />
        <VideoExamples />
        <IndividualServices />
        <CreativePackages />
        <BackToHome />
      </main>
      <Footer />
      {!NEXO_ENABLED && <WhatsAppFloat />}
      <WhatsAppLeadModal
        open={!!waHref}
        href={waHref}
        onClose={() => setWaHref(null)}
      />
    </div>
  );
};

export default OtherServices;
